/** Delete a Job Template from the board row or the Manage header, gated and worded as the legacy trash buttons. */
import { computed, inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { ActivityEntityName, ItemDeletedResponse, JobTemplate, JobTemplateEntity, JobTemplateSecurityRight } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { EntityDataService, EntityManifestService } from '@workbuddy/services/entities';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm';
import { Command } from '@workbuddy/ui/model';
import { ToastService } from '@workbuddy/ui/services';
import { SemanticCommand } from '@workbuddy/ui/services/keyboard.shortcuts';
import { EntityInspectors } from '@workbuddy/ui/v2/blocks/entity.inspector';
import { persistenceErrorMessage, type ActionCommandCapability } from '@workbuddy/ui/v2/surface.engine';

const DELETE_FAILURE = 'An error occurred while deleting the job template';

export function jobTemplateRowCommands(template: JobTemplateEntity): Command[] {

  if (!template?._id || !inject(RightsService).hasRights(JobTemplateSecurityRight.Manage)) return [];
  const injector = inject(Injector);

  return [new Command({
    _id: 'delete',
    label: 'Delete',
    iconLeft: 'fa-regular fa-trash-can',
    action: () => deleteJobTemplate(injector, template),
  })];
}

/** Replaces the generic manage Delete, whose navigate-to-root keeps the inspector outlet and so the deleted template on screen. */
export function createJobTemplateDeleteCommand(injector: Injector, canManage: boolean): ActionCommandCapability<JobTemplate> {

  const manifests = injector.get(EntityManifestService);
  const router = injector.get(Router);

  return {
    descriptor: ({ page }) => computed(() => ({
      // The id is the keymap link for the shift+backspace chord.
      id: SemanticCommand.Delete,
      label: 'Delete',
      iconLeft: 'fa-regular fa-trash-can',
      hidden: !canManage || !page.$model()?._id,
      pin: 'never',
    })),
    run: async page => {

      const template = page.$model();
      if (!canManage || !template?._id) return;
      // Read before the delete: an inspector mount is closed by deleteJobTemplate, only a full page navigates.
      const inInspector = !!injector.get(EntityInspectors, null, { optional: true })?.$activeInspector();
      if (!await deleteJobTemplate(injector, template) || inInspector) return;

      const root = manifests.routes(ActivityEntityName.JobTemplate)?.root;
      if (root) await router.navigate([root]);
    },
  };
}

/** True only on the server's explicit success, so a refused delete never reads as gone; closes the inspector still showing it. */
async function deleteJobTemplate(injector: Injector, template: JobTemplateEntity): Promise<boolean> {

  const toasts = injector.get(ToastService);
  if (!await injector.get(ConfirmServicePro).confirm('Delete Job Template', 'Are you sure you wish to continue?', 'danger')) return false;

  let result: ItemDeletedResponse | null | undefined;
  try {
    result = await injector.get(EntityDataService).getDelete(ActivityEntityName.JobTemplate).mutateAsync({ id: template._id }) as ItemDeletedResponse;
  } catch (error) {
    toasts.error('Job Template Delete Failed', persistenceErrorMessage(error, DELETE_FAILURE));
    return false;
  }
  if (result?.success !== true) {
    toasts.error('Job Template Delete Failed', result?.error ?? DELETE_FAILURE);
    return false;
  }
  toasts.success('Job Template Deleted', `${template.name ?? 'The job template'} was deleted`);

  // Matched after the await: the user may have opened another record while the delete was in flight.
  const inspectors = injector.get(EntityInspectors, null, { optional: true });
  const open = inspectors?.$activeInspector();
  if (open?.entity === ActivityEntityName.JobTemplate && open.id === template._id) await inspectors?.close();
  return true;
}
